import { CARD_API_ENDPOINT } from '@/config/consts'
import { EVC_ADDRESS } from '@/config/euler-contracts'
import { getSubAccountAddress } from '@/domain/euler/shared/subAccounts'
import { waitForTxReceipt } from '@/utils/waitForTxReceipt'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { eulerVaultConnectorAbi } from 'abis/evm/eulerVaultConnector'
import { type Address } from 'viem'
import { useAccount, useChainId, usePublicClient, useReadContract, useSendTransaction } from 'wagmi'
import { CREDIT_TYPES, EULER_CARD_SUB_ACCOUNT_ID } from '../constants'
import { createSmartFetch } from './useAuthenticatedMutation'
import { useCreditReceiver } from './useCreditReceiver'

interface UseEulerWithdrawDelegationParams {
  enabled?: boolean
  subAccountId?: number
  supplyVaultAddress?: Address
}

export function useEulerWithdrawDelegation({
  enabled: externalEnabled = true,
  subAccountId = EULER_CARD_SUB_ACCOUNT_ID,
  supplyVaultAddress,
}: UseEulerWithdrawDelegationParams = {}) {
  const { address } = useAccount()
  const chainId = useChainId()
  const queryClient = useQueryClient()
  const publicClient = usePublicClient()

  const hasValidSubAccount = Number.isInteger(subAccountId)
  const subAccountAddress =
    address && hasValidSubAccount ? getSubAccountAddress(address, subAccountId as number) : undefined

  const { sendTransactionAsync, isPending: isApproving, error: approveError, data: txHash } = useSendTransaction()

  const {
    creditReceiver: operatorAddress,
    isLoading: isLoadingOperator,
    isError: isOperatorError,
  } = useCreditReceiver({
    enabled: externalEnabled,
  })
  const resolvedOperatorAddress = operatorAddress
  const isLoadingResolvedOperator = !resolvedOperatorAddress && isLoadingOperator

  const {
    data: isOperatorAuthorized,
    isLoading: isLoadingAuthorization,
    isSuccess: isAuthorizationLoaded,
  } = useReadContract({
    address: EVC_ADDRESS,
    abi: eulerVaultConnectorAbi,
    functionName: 'isAccountOperatorAuthorized',
    args: subAccountAddress && resolvedOperatorAddress ? [subAccountAddress, resolvedOperatorAddress] : undefined,
    query: {
      enabled: externalEnabled && !!subAccountAddress && !!resolvedOperatorAddress,
    },
  })

  const hasLoadedAuthorization = (isAuthorizationLoaded && !isLoadingResolvedOperator) || isOperatorError
  const isSetupComplete = hasLoadedAuthorization && !!isOperatorAuthorized
  const needsDelegation = hasLoadedAuthorization && !isOperatorAuthorized

  const approveDelegation = useMutation({
    mutationFn: async () => {
      if (!address) {
        throw new Error('Wallet not connected')
      }
      if (!supplyVaultAddress) {
        throw new Error('Supply vault address not available')
      }
      if (!publicClient) {
        throw new Error('Public client not available')
      }

      try {
        const delegateFetch = createSmartFetch(`${CARD_API_ENDPOINT}/v1/vendor/cards/users/credit/delegate`, {
          method: 'POST',
          body: JSON.stringify({
            creditType: CREDIT_TYPES.EULER_SPEND,
            creditLimit: 0,
            chainID: chainId,
            userParams: {
              supplyVaultAddress,
              subAccountId,
            },
          }),
        })

        const apiData = await delegateFetch({})
        if (!apiData?.data?.to || !apiData?.data?.callData) {
          throw new Error('Invalid delegation response from server')
        }
        const { to, callData, value } = apiData.data

        const hash = await sendTransactionAsync({
          to,
          value: BigInt(value || 0),
          data: callData,
        })

        await waitForTxReceipt(publicClient, hash)

        // Await the refetch so consumers that read `needsDelegation` see the
        // post-approval state as soon as `mutateAsync` resolves. Without the
        // await, there's a brief window where the button re-enables while the
        // on-chain read is still catching up, which can enqueue duplicate txs.
        await queryClient.invalidateQueries({
          queryKey: ['readContract'],
        })

        return hash
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        if (
          errorMessage.includes('rejected') ||
          errorMessage.includes('denied') ||
          errorMessage.includes('cancelled')
        ) {
          throw new Error('User rejected the operator authorization request')
        }
        throw error
      }
    },
  })

  const resetError = () => {
    approveDelegation.reset()
  }

  const isMutationPending = approveDelegation.isPending
  const isConfirming = !isApproving && isMutationPending
  const isConfirmed = approveDelegation.isSuccess

  return {
    isOperatorAuthorized: !!isOperatorAuthorized,
    isSetupComplete,
    needsDelegation,
    isLoadingAuthorization: isLoadingAuthorization || (isLoadingResolvedOperator && !isOperatorError),
    isAuthorizationLoaded: hasLoadedAuthorization,
    approveDelegation: approveDelegation.mutateAsync,
    isApproving,
    isConfirming,
    isConfirmed,
    txHash,
    approveError: approveError || approveDelegation.error,
    resetError,
    delegationTarget: resolvedOperatorAddress,
    subAccountAddress,
  }
}
